import { slugify } from '@protector/utils/string';
import type { Crop } from '@protector/utils/types';
import { snakeCase } from 'lodash';
import { isGrapeCrop, isPotatoCrop } from './utils.ts';

export type GrowthStageTranslate = (key: string, defaultValue: string) => string;

type GrowthStageCrop = Pick<Crop, 'wk_slug'> | null;

const getNormalizedStage = (stage: string | number): number => {
  if (typeof stage === 'string') {
    const parsedStageNumString = stage.replace(/^(BBCH\s|BBCH-)/, '').trim();

    return /^\d{1,2}-\d{1,2}$/.test(parsedStageNumString)
      ? Number.parseInt(parsedStageNumString, 10) + 0.5
      : Number.parseFloat(parsedStageNumString);
  }

  return +stage;
};

const getPotatoGrowthStage = (translate: GrowthStageTranslate, stage: number | string, isAbbreviated: boolean): string =>
  translate(`general.growth_stages_potato.${slugify(stage.toString())}.${isAbbreviated ? 'abbreviated' : 'full'}`, stage.toString());

const getLocalizedGSString = (
  translate: GrowthStageTranslate,
  stage: number | string,
  crop: GrowthStageCrop,
  isAbbreviated: boolean
): string => {
  if (crop && isPotatoCrop(crop)) return getPotatoGrowthStage(translate, stage, isAbbreviated);

  if (!stage && !(stage === 0 || stage === '0')) {
    return '--';
  }

  const normalizedStageNum = getNormalizedStage(stage);

  const rootSlug = crop && isGrapeCrop(crop) ? 'general.growth_stages_grapes' : 'general.growth_stages';

  if (!Number.isNaN(normalizedStageNum)) {
    const normalizedStage = Number.isInteger(normalizedStageNum) ? `${normalizedStageNum}` : `${Math.floor(normalizedStageNum)}_5`;
    const defaultValue = Number.isInteger(normalizedStageNum)
      ? `BBCH ${normalizedStageNum}`
      : `BBCH ${Math.floor(normalizedStageNum)}-${Math.floor(normalizedStageNum) + 1}`;
    const bbchSlug = `bbch_${normalizedStage}`;
    return translate(`${rootSlug}.${bbchSlug}.${isAbbreviated ? 'abbreviated' : 'full'}`, defaultValue);
  }

  const slug = snakeCase(String(stage)).toLowerCase();
  return translate(`${rootSlug}.text.${slug}`, `${stage}`);
};

/** Half stages (`32.5`, `"BBCH-32.5"`) render as their range (`BBCH 32-33`), the label every client shows. */
export function getCerealsGrowthStageTranslation(translate: GrowthStageTranslate, bbch: number | string, useAbbreviation = false): string {
  return getLocalizedGSString(translate, bbch, null, useAbbreviation);
}

export function getCropGrowthStageTranslation(
  translate: GrowthStageTranslate,
  bbch: number | string,
  crop: GrowthStageCrop,
  useAbbreviation = false
): string {
  return getLocalizedGSString(translate, bbch, crop, useAbbreviation);
}
